import { useEffect, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { FilterPanel } from './FilterPanel';

type MonthFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
};

/**
 * Single-month column filter, applied as `field[eq]` on the first day of the
 * month — the shape monthly settlement buckets are stored in.
 */
export const MonthFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
}: MonthFilterProps) => {
  const t = useTranslate();
  const current = getFilterValue<string>(filters, field, 'eq');
  const [pending, setPending] = useState<Dayjs | null>(
    current ? dayjs(current) : null
  );

  useEffect(() => setPending(current ? dayjs(current) : null), [current]);

  const handleFilter = () => {
    setFilters(
      [
        {
          field,
          operator: 'eq',
          value: pending?.startOf('month').format('YYYY-MM-DD'),
        },
      ],
      'merge'
    );
    confirm();
  };

  const handleReset = () => {
    setPending(null);
    setFilters([{ field, operator: 'eq', value: undefined }], 'merge');
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <DatePicker
        picker="month"
        value={pending}
        onChange={setPending}
        placeholder={placeholder ?? t('base.filter.from', 'Od')}
        style={{ width: 200 }}
      />
    </FilterPanel>
  );
};
